import { Eye, FileText, ImageOff, Play } from "lucide-react"
import { useState } from "react"
import { cn } from "@/shared/lib/utils"
import { TelegramMarkdown } from "@/shared/ui/telegram-markdown"
import { SlidingPill } from "@/shared/ui/sliding-pill"
import { LoadSwap } from "@/shared/ui/load-swap"

export interface PreviewMedia {
	key: string
	kind: "photo" | "video" | "file"
	/** Картинка: само фото или обложка видео. */
	imageUrl?: string | null
	name?: string | null
}

export interface PreviewContent {
	title: string
	shortDescription: string
	description: string
	coverUrl: string | null
	media: PreviewMedia[]
	formats: string[]
}

type Mode = "telegram" | "app"

const MODES: Array<{ value: Mode; label: string }> = [
	{ value: "telegram", label: "Канал" },
	{ value: "app", label: "Приложение" },
]

function MediaTile({ item, className }: { item: PreviewMedia; className?: string }) {
	return (
		<div className={cn("relative overflow-hidden bg-black/40", className)}>
			{item.imageUrl ? (
				<img src={item.imageUrl} alt="" className="size-full object-cover" />
			) : (
				<div className="grid size-full place-items-center text-white/50">
					{item.kind === "file" ? <FileText className="size-5" /> : <ImageOff className="size-5" />}
				</div>
			)}
			{item.kind === "video" ? (
				<span className="absolute inset-0 grid place-items-center">
					<span className="grid size-10 place-items-center rounded-full bg-black/55 text-white backdrop-blur">
						<Play className="ml-0.5 size-4 fill-current" />
					</span>
				</span>
			) : null}
		</div>
	)
}

/** Альбом как в Telegram: одна картинка во всю ширину, две рядом, три и больше сеткой. */
function Album({ media }: { media: PreviewMedia[] }) {
	const visual = media.filter((item) => item.kind !== "file").slice(0, 4)
	if (visual.length === 0) return null
	if (visual.length === 1) return <MediaTile item={visual[0]!} className="aspect-[4/3] w-full" />
	return (
		<div className={cn("grid gap-0.5", visual.length === 2 ? "grid-cols-2" : "grid-cols-2")}>
			{visual.map((item, index) => (
				<MediaTile
					key={item.key}
					item={item}
					className={cn(
						"aspect-square",
						visual.length === 3 && index === 0 && "col-span-2 aspect-[2/1]",
					)}
				/>
			))}
		</div>
	)
}

function TelegramView({ content }: { content: PreviewContent }) {
	const files = content.media.filter((item) => item.kind === "file")
	return (
		<div className="rounded-2xl bg-[#0e1621] p-3">
			<div className="overflow-hidden rounded-xl rounded-bl-sm bg-[#182533] text-[14px] leading-[1.4] text-white">
				<Album media={content.media} />
				{files.map((file) => (
					<div key={file.key} className="flex items-center gap-2.5 px-3 pt-2.5">
						<span className="grid size-9 place-items-center rounded-full bg-[#2b5278]">
							<FileText className="size-4" />
						</span>
						<span className="truncate text-sm">{file.name || "Файл"}</span>
					</div>
				))}
				<div className="px-3 pt-2 pb-1.5">
					{content.description ? (
						<TelegramMarkdown className="break-words whitespace-pre-wrap">
							{content.description}
						</TelegramMarkdown>
					) : (
						<p className="text-white/40">Текст поста появится здесь</p>
					)}
					<p className="mt-1 flex items-center justify-end gap-1 text-[11px] text-white/45">
						<Eye className="size-3" /> 1,2K · 14:02
					</p>
				</div>
			</div>
		</div>
	)
}

function AppView({ content }: { content: PreviewContent }) {
	return (
		<div className="space-y-4">
			<div className="overflow-hidden rounded-2xl border border-border bg-background">
				<div className="aspect-video bg-muted">
					{content.coverUrl ? (
						<img src={content.coverUrl} alt="" className="size-full object-cover" />
					) : (
						<div className="grid size-full place-items-center text-xs text-muted-foreground">
							Нет обложки
						</div>
					)}
				</div>
				<div className="space-y-1.5 p-3.5">
					{content.formats.length ? (
						<div className="flex flex-wrap gap-1">
							{content.formats.slice(0, 3).map((format) => (
								<span
									key={format}
									className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary"
								>
									{format}
								</span>
							))}
						</div>
					) : null}
					<p
						className={cn("line-clamp-2 font-semibold", !content.title && "text-muted-foreground")}
					>
						{content.title || "Заголовок материала"}
					</p>
					{content.shortDescription ? (
						<p className="line-clamp-3 text-sm text-muted-foreground">{content.shortDescription}</p>
					) : null}
				</div>
			</div>
			<p className="text-xs text-muted-foreground">
				Так материал выглядит в каталоге мини-аппа и мобильного приложения.
			</p>
		</div>
	)
}

/** Предпросмотр материала на площадках. Переключатель помнит выбор между экранами. */
export function PostPreview({
	content,
	className,
}: {
	content: PreviewContent
	className?: string
}) {
	const [mode, setMode] = useState<Mode>(() => {
		try {
			return (localStorage.getItem("post-preview-mode") as Mode) || "telegram"
		} catch {
			return "telegram"
		}
	})
	const choose = (next: Mode) => {
		setMode(next)
		try {
			localStorage.setItem("post-preview-mode", next)
		} catch {}
	}

	return (
		<section className={cn("space-y-3", className)} aria-label="Предпросмотр">
			<div className="flex items-center justify-between gap-3">
				<h2 className="text-sm font-medium text-muted-foreground">Предпросмотр</h2>
				<div role="tablist" className="relative relative flex rounded-full bg-secondary p-0.5">
					<SlidingPill className="rounded-full" />
					{MODES.map((item) => (
						<button
							key={item.value}
							type="button"
							role="tab"
							aria-selected={mode === item.value}
							data-active={mode === item.value}
							onClick={() => choose(item.value)}
							className={cn(
								"relative rounded-full px-3 py-1 text-xs font-medium transition-colors",
								mode === item.value
									? "text-foreground"
									: "text-muted-foreground hover:text-foreground",
							)}
						>
							<span className="relative">{item.label}</span>
						</button>
					))}
				</div>
			</div>
			{/* Канал и приложение сменяются перекрёстно, а высота тянется к новому виду:
			    блок «Готовность» ниже не прыгает. */}
			<LoadSwap loading={false} id={mode}>
				{mode === "telegram" ? <TelegramView content={content} /> : <AppView content={content} />}
			</LoadSwap>
		</section>
	)
}
